import { Subject } from 'rxjs';

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatTable } from '@angular/material/table';

import { snDetailExpand, snFadeIn, snFadeOut, snListStagger } from '@lib/common/animations';
import { SnTableBaseComponent } from '@lib/core';

import { SnLpr, SnLprObject } from '../../api';

@Component({
  selector: 'fs-lpr-hotlists-plates-table',
  templateUrl: './lpr-hotlists-plates-table.component.html',
  styleUrls: ['./lpr-hotlists-plates-table.component.scss'],
  animations: [snListStagger, snFadeIn, snFadeOut, snDetailExpand],
})
export class LprHotlistsPlatesTableComponent
  extends SnTableBaseComponent<SnLprObject>
  implements OnDestroy, OnInit
{
  @ViewChild(MatTable, { read: ElementRef }) public matTableRef: ElementRef;

  @Input()
  public hotlist: SnLpr;

  public defaultColumns = ['plate', 'plateState', 'reason', 'createdBy', 'expiresAt', 'actions'];

  public displayedColumns = [];

  public pressed = false;
  public currentResizeIndex: number;
  public startX: number;
  public startWidth: number;
  public isResizingRight: boolean;
  public resizableMousemove: () => void;
  public resizableMouseup: () => void;

  private readonly _destroy$ = new Subject();

  constructor(private readonly renderer: Renderer2) {
    super();
  }

  @HostListener('window:resize', ['$event'])
  public onResize(): void {
    if (this.matTableRef) {
      this.setTableResize(this.matTableRef.nativeElement.clientWidth);
    }
  }

  public ngOnInit(): void {
    this.displayedColumns = this.columns.map((field) => {
      return {
        field,
        width: 200,
      };
    });
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public trackBy(index: number, item: SnLprObject): any {
    return item.id;
  }

  public getField(item: SnLprObject, field: string): string {
    if (!item.properties.hotlistData) {
      return '';
    }
    return item.properties.hotlistData[field];
  }

  public isDefault(column: string): boolean {
    return this.defaultColumns.includes(column);
  }

  public reorderColumns(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.columns, event.previousIndex, event.currentIndex);
    this.displayedColumns = [
      ...this.columns.map((field) => {
        return {
          field,
          width: 200,
        };
      }),
    ];
  }

  public mouseMove(index: number): void {
    this.resizableMousemove = this.renderer.listen('document', 'mousemove', (event) => {
      if (this.pressed && event.buttons) {
        const dx = this.isResizingRight ? event.pageX - this.startX : -event.pageX + this.startX;
        const width = this.startWidth + dx;
        if (this.currentResizeIndex === index && width > 50) {
          this.setColumnWidthChanges(index, width);
        }
      }
    });
    this.resizableMouseup = this.renderer.listen('document', 'mouseup', () => {
      if (this.pressed) {
        this.pressed = false;
        this.currentResizeIndex = -1;
        this.resizableMousemove();
        this.resizableMouseup();
      }
    });
  }

  public onResizeColumn(event: any, index: number): void {
    this._checkResizing(event, index);
    this.currentResizeIndex = index;
    this.pressed = true;
    this.startX = event.pageX;
    this.startWidth = event.target.clientWidth;
    event.preventDefault();
    this.mouseMove(index);
  }

  public setColumnWidthChanges(index: number, width: number): void {
    const columns = this.displayedColumns;
    const orgWidth = columns[index].width;

    const dx = width - orgWidth;
    if (dx !== 0) {
      const j = this.isResizingRight ? index + 1 : index - 1;
      const newWidth = columns[j].width - dx;
      if (newWidth > 50) {
        columns[index].width = width;
        this.setColumnWidth(columns[index]);
        columns[j].width = newWidth;
        this.setColumnWidth(columns[j]);
      }
    }
  }

  public setColumnWidth(column: any): void {
    const columnEls = Array.from(document.getElementsByClassName('mat-column-' + column.field));
    columnEls.forEach((el: HTMLDivElement) => {
      el.style.width = column.width + 'px';
    });
  }

  public setTableResize(tableWidth: number): void {
    const columns = this.displayedColumns;
    let totWidth = 0;
    columns.forEach((column) => {
      totWidth += column.width;
    });
    const scale = (tableWidth - 5) / totWidth;
    columns.forEach((column) => {
      column.width *= scale;
      this.setColumnWidth(column);
    });
  }

  private _checkResizing(event: any, index: number): void {
    const cellData = this._getCellData(index);
    if (
      index === 0 ||
      (Math.abs(event.pageX - cellData.right) < cellData.width / 2 &&
        index !== this.columns.length - 1)
    ) {
      this.isResizingRight = true;
    } else {
      this.isResizingRight = false;
    }
  }

  private _getCellData(index: number): any {
    const headerRow = this.matTableRef.nativeElement.children[0];
    const cell = headerRow.children[index];
    return cell.getBoundingClientRect();
  }
}
